import { useState } from 'react';
import { GaugeCanvas } from './GaugeCanvas';
import { SpeedTestCards } from './SpeedTestCards';
import { ServerInfo } from './ServerInfo';
import { ResultsPanel } from './ResultsPanel';
import { useSpeedTest } from './useSpeedTest';

export function SpeedTest() {
  const { state, updateState, doPing, doDns, doDownload, doUpload } = useSpeedTest();
  const [liveSpeed, setLiveSpeed] = useState(0);

  const startTest = async () => {
    if (state.running) return;

    updateState({
      running: true,
      phase: 'INITIALIZING',
      progress: 0,
      results: null,
      ping: 0,
      download: 0,
      upload: 0,
      jitter: 0,
      loss: 0,
      bloat: 0,
      dns: 0,
    });

    setLiveSpeed(0);

    try {
      // 1. Ping Test
      updateState({ phase: 'PING TEST', progress: 5 });
      const { ping, jitter, loss } = await doPing(12);
      updateState({ ping, jitter, loss });

      // 2. DNS Test
      updateState({ phase: 'DNS TEST', progress: 8 });
      const dns = await doDns();
      updateState({ dns });

      // 3. Download Test
      updateState({ phase: 'DOWNLOAD', progress: 10 });
      let dlSmooth = 0;
      const download = await doDownload((mbps) => {
        dlSmooth = dlSmooth * 0.35 + mbps * 0.65;
        setLiveSpeed(dlSmooth);
        updateState({ download: dlSmooth, progress: 10 + ((mbps / 100) * 40) % 40 });
      });
      updateState({ download });
      setLiveSpeed(0);

      // 4. Bloat Test
      updateState({ phase: 'BUFFER BLOAT', progress: 52 });
      const bloat = ping * 1.2; // Simplified for now
      updateState({ bloat });

      // 5. Upload Test
      updateState({ phase: 'UPLOAD', progress: 55 });
      let ulSmooth = 0;
      const upload = await doUpload((mbps) => {
        ulSmooth = ulSmooth * 0.35 + mbps * 0.65;
        setLiveSpeed(ulSmooth);
        updateState({ upload: ulSmooth, progress: 55 + ((mbps / 100) * 40) % 40 });
      });
      updateState({ upload });
      setLiveSpeed(0);

      // Store results
      const bestStream = download > 100 ? '4K' : download > 25 ? '1080p' : download > 5 ? '720p' : '480p';
      updateState({
        phase: 'COMPLETE',
        progress: 100,
        results: {
          ping,
          jitter,
          loss,
          download,
          upload,
          bloat,
          dns,
          bestStream,
          timestamp: new Date(),
        },
        running: false,
      });
    } catch (err) {
      console.error(err);
      updateState({ running: false, phase: 'ERROR' });
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto px-3 py-6 space-y-4">
      {/* Header */}
      <div className="text-center space-y-2 mb-6">
        <h1 className="text-3xl font-black bg-gradient-to-r from-[hsl(var(--st-cyan))] to-[hsl(var(--st-purple))] bg-clip-text text-transparent tracking-widest">
          SPEEDTEST PRO
        </h1>
        <p className="text-xs text-[hsl(var(--st-dim))] uppercase tracking-wider">
          Real-time internet speed measurement
        </p>
      </div>

      {/* Server Info */}
      <ServerInfo />

      {/* Gauge */}
      <div className="flex justify-center">
        <div className="bg-[hsl(var(--st-surface-1))] rounded-lg p-2">
          <GaugeCanvas speed={state.running ? liveSpeed : state.results?.download || 0} />
          <div className="text-center mt-2 space-y-1">
            <div className="text-4xl font-black text-white">
              {state.running
                ? liveSpeed < 10
                  ? liveSpeed.toFixed(1)
                  : Math.round(liveSpeed)
                : state.results?.download
                  ? state.results.download.toFixed(1)
                  : '0'}
            </div>
            <div className="text-xs text-[hsl(var(--st-dim))]">MBPS</div>
            <div className="text-xs tracking-widest text-[hsl(var(--st-cyan))]">
              {state.phase}
            </div>
          </div>
        </div>
      </div>

      {/* Cards */}
      <SpeedTestCards
        ping={state.ping}
        download={state.download}
        upload={state.upload}
        activePing={state.phase === 'PING TEST'}
        activeDl={state.phase === 'DOWNLOAD'}
        activeUl={state.phase === 'UPLOAD'}
      />

      {/* Progress Bar */}
      {state.running && (
        <div className="h-1 bg-[hsl(var(--st-surface-2))] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[hsl(var(--st-cyan))] to-[hsl(var(--st-pink))] transition-all duration-300"
            style={{ width: `${state.progress}%` }}
          />
        </div>
      )}

      {/* Start Button */}
      <div className="flex justify-center pt-2">
        <button
          onClick={startTest}
          disabled={state.running}
          className="relative px-12 py-3 font-black uppercase tracking-widest text-lg border-2 border-[hsl(var(--st-cyan))] text-[hsl(var(--st-cyan))] rounded-full transition-all hover:bg-[hsl(var(--st-cyan))] hover:text-[hsl(var(--st-bg))] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {state.results ? 'TEST AGAIN' : 'GO'}
          {state.running && (
            <span className="absolute inset-0 border-2 border-[hsl(var(--st-cyan))] rounded-full animate-ping opacity-75" />
          )}
        </button>
      </div>

      {/* Results */}
      {state.results && (
        <ResultsPanel
          ping={state.results.ping}
          jitter={state.results.jitter}
          loss={state.results.loss}
          download={state.results.download}
          upload={state.results.upload}
          bloat={state.results.bloat}
          dns={state.results.dns}
          bestStream={state.results.bestStream}
          timestamp={state.results.timestamp}
          visible={!!state.results}
        />
      )}
    </div>
  );
}
